import React, { useEffect, useState, useCallback, useRef } from "react";
import { SessionProvider, useSession } from "./contexts/SessionContext";
import { AudioSettingsProvider } from "./contexts/AudioSettingsContext";
import { AuthProvider } from "./contexts/AuthContext";
import { DashboardHeader } from "./components/DashboardHeader";
import { TranscriptionBoard } from "./components/TranscriptionBoard";
import { GreetingsPanel } from "./components/GreetingsPanel";
import OnCallSoundboardStrip from "./components/OnCallSoundboardStrip";
import { NotePad } from "./components/NotePad";
import { DictionaryTool } from "./components/DictionaryTool";
import { SilenceGuardian } from "./components/SilenceGuardian";
import { SttStallBanner } from "./components/SttStallBanner";
import { AutopilotGuard } from "./components/AutopilotGuard";
import { DeskExerciseWidget } from "./components/DeskExerciseWidget";
import { RosaryWidget } from "./components/RosaryWidget";
import { MealTrackerWidget } from "./components/MealTrackerWidget";
import { ChoreTrackerWidget } from "./components/ChoreTrackerWidget";
import {
  OFF_CALL_VIEWS,
  loadWorkspaceView,
  saveWorkspaceView,
  hasSeenStudioHint,
  markStudioHintSeen,
} from "./components/WorkspaceViewSwitcher";
import { useDeepgram } from "./hooks/useDeepgram";
import { useAudioSource } from "./hooks/useAudioSource";
import { useDevSimulate } from "./hooks/useDevSimulate";
import { useProgressiveAudio } from "./hooks/useProgressiveAudio";
import { useAppUpdateCheck } from "./hooks/useAppUpdateCheck";
import { useReleaseNotes } from "./hooks/useReleaseNotes";
import { UpdateAppBanner } from "./components/UpdateAppBanner";
import { ReleaseNotesModal } from "./components/ReleaseNotesModal";
import { loadFile, generateObjectUrl } from "./utils/storage";
import { resolveAppBackgroundPath } from "./utils/defaultBackgrounds";
import SettingsPanel from "./components/SettingsPanel";
import { OffCallWorkspace } from "./components/OffCallWorkspace";
import { GoalTrackingView, GOALS_VIEW } from "./components/GoalTrackingView";
import GreetingEditorView, { GREETING_EDITOR_VIEW } from "./components/GreetingEditorView";
import { SplashScreen } from "./components/SplashScreen";
import { GuideHostProvider } from "./contexts/GuideHostContext";
import { ElementHintProvider } from "./components/ElementHint";
import { HudInspectorHost } from "./components/HudInspector";
import { SttAudioRecordingIndicator, SttDiagnosticInspector } from "./components/SttDiagnosticInspector";
import { isSplashSeenThisSession } from "./utils/splashStorage";
import { isAppGuideDone } from "./utils/appGuideStorage";
import { getReleaseNoteForVersion } from "./content/releaseNotes";
import { shouldShowReleaseNotes } from "./utils/releaseNotesStorage";
import {
  getRuntimeDeepgramKey,
  hasConfiguredDeepgramKey,
  isValidDeepgramApiKey,
  isRememberExpired,
  needsUserSuppliedDeepgramKey,
} from "./utils/deepgramRuntimeKey";
import { getDeepgramBlockReason, getDeepgramSettingsPrompt } from "./utils/deepgramSettingsPrompt";
import { applyThemePalette, loadThemePalette } from "./utils/themePalette";
import { setSttActive } from "./utils/routeDiagnostics";
import { shouldHealConnect } from "./utils/connectEvidence";
import { isWellbeingDockEnabled } from "./utils/wellbeingDock";
import {
  isComponentVisible,
  useComponentVisibilityRefresh,
} from "./utils/componentVisibility";
import "./index.css";

const focusQuickNotesSoon = () => {
  window.setTimeout(() => {
    window.dispatchEvent(new Event("catint_focus_notes"));
  }, 80);
};

const Dashboard = () => {
  const {
    startRecording,
    startRecordingFresh,
    stopRecording,
    reconnectStream,
    armCaptionCapture,
    captions,
    clearCaptions,
    sttLanguage,
    toggleLanguage,
    connectionState,
    connectionMessage,
    sttLive,
    apiKeyRejected,
    connectProgress,
    lastDataTime,
    sttStall,
    micTestMode,
    setMicTestMode,
    tabStreamReady,
    cableStreamReady,
    attachedAudioSourceMode,
    virtualCableFailure,
    switchAudioSourceModeSafely,
  } = useDeepgram();
  useDevSimulate();
  const {
    currentSourceMode: configuredAudioSourceMode,
    switchAudioSourceMode,
  } = useAudioSource();
  const {
    isNotesOpen,
    setIsNotesOpen,
    isActive,
    hipaaGraceActive,
    isBreakActive,
    isZombieCall,
    minutesSinceLastBreak,
    startSession,
    cancelHipaaDisconnectGrace,
    clearZombieState,
    callFocusMode,
    stopBreak,
    autoAttachEnabled,
  } = useSession();
  const { playCoin } = useProgressiveAudio();
  const { updateAvailable, latestVersionToken, dismissUpdate, reloadToUpdate } =
    useAppUpdateCheck();
  const [isEditingBg, setIsEditingBg] = useState(false);
  const [workspaceView, setWorkspaceView] = useState(loadWorkspaceView);
  const resetUiState = useCallback(() => {
    // UI-only reset: do NOT touch Deepgram/API keys or transcripts.
    try {
      localStorage.setItem('catint_active', 'false');
    } catch {}

    [
      'catint_workspace_view',
      'catint_workspace_initialized',
      'catint_studio_hint_seen',
      'catint_break',
      'catint_hold',
    ].forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {}
    });

    try {
      // Ensure immediate UI mode switches before reload.
      window.dispatchEvent(new CustomEvent('catint_ui_reset_started'));
    } catch {}

    try {
      window.location.reload();
    } catch {
      // In case reload is blocked, at least clear zombie UI locally.
      clearZombieState?.();
    }
  }, [clearZombieState]);

  // Dev helper (and troubleshooting) for persisted runtime-state mess.
  useEffect(() => {
    try {
      window.catintResetUiState = resetUiState;
    } catch {
      // ignore
    }
  }, [resetUiState]);
  const [showStudioHint, setShowStudioHint] = useState(
    () => !hasSeenStudioHint(),
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  // v4.161.0: null = no explicit target, so SettingsPanel reopens the last
  // panel the operator used instead of snapping back to Deepgram every time.
  const [settingsSection, setSettingsSection] = useState(null);
  const [settingsOpenReason, setSettingsOpenReason] = useState(null);
  const [settingsOpenTrigger, setSettingsOpenTrigger] = useState("general");
  const [deepgramKeyNotice, setDeepgramKeyNotice] = useState("");
  const [showWellbeingDock, setShowWellbeingDock] = useState(isWellbeingDockEnabled);
  const [quickNotesNotice, setQuickNotesNotice] = useState("");
  const [, setRuntimeKeyTick] = useState(0);
  const [shellReady, setShellReady] = useState(() => isSplashSeenThisSession());
  const [showSplash, setShowSplash] = useState(() => !isSplashSeenThisSession());
  const {
    note: releaseNote,
    open: releaseNotesOpen,
    handleGotIt: onReleaseNotesGotIt,
    handleLater: onReleaseNotesLater,
    handleNever: onReleaseNotesNever,
  } = useReleaseNotes({ shellReady, isActive, isZombieCall });
  useComponentVisibilityRefresh();

  useEffect(() => {
    setSttActive(connectionState === "connected");
    return () => setSttActive(false);
  }, [connectionState]);

  const showWellbeingWidgets =
    showWellbeingDock &&
    isComponentVisible('wellbeing_dock', { isActive, isZombieCall });

  const showQuickNotesNotice = useCallback((message) => {
    setQuickNotesNotice(message);
    window.setTimeout(() => setQuickNotesNotice(""), 3200);
  }, []);

  const toggleQuickNotes = useCallback(() => {
    setIsNotesOpen((open) => {
      const next = !open;
      if (next) {
        if (isEditingBg) {
          showQuickNotesNotice("Quick notes blocked: close the soundboard/background editor first.");
        } else {
          showQuickNotesNotice("Quick notes opened.");
          focusQuickNotesSoon();
        }
      }
      return next;
    });
  }, [isEditingBg, setIsNotesOpen, showQuickNotesNotice]);

  useEffect(() => {
    if (isNotesOpen && !isEditingBg) focusQuickNotesSoon();
  }, [isEditingBg, isNotesOpen]);
  useEffect(() => {
    const onRuntime = () => setRuntimeKeyTick((t) => t + 1);
    const applySettingsOpen = (detail = {}) => {
      // v4.161.0: `panel` is a deep link to one panel; `section` is the older
      // name for the same thing. No target at all => SettingsPanel restores the
      // last panel the operator used instead of snapping back to Deepgram.
      setSettingsSection(detail.panel || detail.section || null);
      setSettingsOpenReason(detail.reason || null);
      setSettingsOpenTrigger(detail.trigger || "general");
      setSettingsOpen(true);
    };
    const onShowVault = (e) => applySettingsOpen({ section: "deepgram", ...e?.detail });
    const onShowSettings = (e) => applySettingsOpen({ section: "deepgram", ...e?.detail });
    const onShowLanguageSettings = (e) => {
      applySettingsOpen({ section: "language", reason: null, trigger: "general", ...e?.detail });
    };

    window.addEventListener("cat_deepgram_runtime_key_changed", onRuntime);
    window.addEventListener("cat_show_deepgram_key_vault", onShowVault);
    window.addEventListener("cat_show_settings", onShowSettings);
    window.addEventListener("cat_show_language_settings", onShowLanguageSettings);
    const onWellbeingDock = () => setShowWellbeingDock(isWellbeingDockEnabled());
    window.addEventListener("cat_wellbeing_dock_changed", onWellbeingDock);
    window.addEventListener("cat_personal_dock_changed", onWellbeingDock);
    const onKeyNeeded = (e) => {
      const trigger = e?.detail?.trigger || "connect";
      const prompt = getDeepgramSettingsPrompt(e?.detail?.reason, trigger);
      setDeepgramKeyNotice(
        prompt?.body || "Deepgram key missing — open Settings (gear) to paste your key.",
      );
      window.setTimeout(() => setDeepgramKeyNotice(""), 8000);
    };
    window.addEventListener("cat_deepgram_key_needed", onKeyNeeded);
    return () => {
      window.removeEventListener("cat_deepgram_runtime_key_changed", onRuntime);
      window.removeEventListener("cat_show_deepgram_key_vault", onShowVault);
      window.removeEventListener("cat_show_settings", onShowSettings);
      window.removeEventListener("cat_show_language_settings", onShowLanguageSettings);
      window.removeEventListener("cat_wellbeing_dock_changed", onWellbeingDock);
      window.removeEventListener("cat_personal_dock_changed", onWellbeingDock);
      window.removeEventListener("cat_deepgram_key_needed", onKeyNeeded);
    };
  }, []);

  const hasRuntimeKey = isValidDeepgramApiKey(getRuntimeDeepgramKey());
  const apiKeyMissing = needsUserSuppliedDeepgramKey();

  useEffect(() => {
    if (hasRuntimeKey) {
      setSettingsOpen(false);
      setSettingsOpenReason(null);
    }
  }, [hasRuntimeKey]);

  // UX: stale tab flag is cleared in useDeepgram when no live stream exists.

  const offCallWorkspace = isActive || isZombieCall ? null : workspaceView;
  const isSoundboardStudio = offCallWorkspace === "soundboard";
  // v4.113.0: transient goal tracking view — never persisted (refresh → scoreboard).
  const isGoalsView = offCallWorkspace === GOALS_VIEW;

  const cycleWorkspaceView = useCallback(() => {
    if (isActive || isZombieCall) return;
    markStudioHintSeen();
    setShowStudioHint(false);
    setWorkspaceView((prev) => {
      const i = OFF_CALL_VIEWS.indexOf(prev);
      const next = OFF_CALL_VIEWS[(i + 1) % OFF_CALL_VIEWS.length];
      saveWorkspaceView(next);
      return next;
    });
  }, [isActive, isZombieCall]);

  const exitSoundboardStudio = useCallback(() => {
    if (isActive || isZombieCall) return;
    markStudioHintSeen();
    setShowStudioHint(false);
    saveWorkspaceView("scoreboard");
    setWorkspaceView("scoreboard");
  }, [isActive, isZombieCall]);

  // v4.113.0: Goal Tracking view — off-call only (same gating as Soundboard).
  // 'goals' is transient: saveWorkspaceView ignores it, so a refresh restores scoreboard.
  // Toggle semantics (like onOpenSoundboard): clicking 🎯 again — or the cat — exits.
  // v4.163.0: the goal wheel is off-call only, and FIVE header entry points can
  // be pressed mid-call (the 🎯 chip, the m7 metric cell, the DAILY income card,
  // the 📅, and Settings → Goals). They all called this and got silence. Now
  // they all say why, in one place, instead of five times in five files.
  // v4.167.0: generalised — the Soundboard and Greeting Editor are off-call too,
  // and their header buttons are gone, so Settings is their only door.
  const [workspaceBlockedNotice, setWorkspaceBlockedNotice] = useState('');

  const sayWorkspaceBlocked = useCallback((what) => {
    setWorkspaceBlockedNotice(`${what} is off-call only — stop the call first.`);
    window.setTimeout(() => setWorkspaceBlockedNotice(''), 4000);
  }, []);

  const onOpenGoalsView = useCallback(() => {
    if (isActive || isZombieCall) {
      sayWorkspaceBlocked('The goal wheel');
      return;
    }
    setWorkspaceView((prev) => {
      if (prev === GOALS_VIEW) {
        saveWorkspaceView("scoreboard");
        return "scoreboard";
      }
      return GOALS_VIEW;
    });
  }, [isActive, isZombieCall, sayWorkspaceBlocked]);

  // v4.167.0: the Soundboard Studio's door. Its header button (🎛) was the only
  // way in, and v4.166.0 thinned that row to free up header width — so Settings
  // → Studio is the new way in. Same shape as cat_open_goals_view: OPENS the
  // view, never toggles it shut, so a stale click cannot bounce the operator out.
  useEffect(() => {
    const onOpenStudio = () => {
      if (isActive || isZombieCall) {
        sayWorkspaceBlocked('Soundboard Studio');
        return;
      }
      markStudioHintSeen();
      setShowStudioHint(false);
      setWorkspaceView((prev) => (prev === "soundboard" ? prev : "soundboard"));
    };
    window.addEventListener("cat_open_soundboard_view", onOpenStudio);
    return () => window.removeEventListener("cat_open_soundboard_view", onOpenStudio);
  }, [isActive, isZombieCall, sayWorkspaceBlocked]);

  const exitGoalsView = useCallback(() => {
    if (isActive || isZombieCall) return;
    saveWorkspaceView("scoreboard");
    setWorkspaceView("scoreboard");
  }, [isActive, isZombieCall]);

  // v4.161.0 — deep link into the goal wheel. Settings → Goals (or any other
  // caller) dispatches this instead of reaching into state. Only ever OPENS the
  // view, never toggles it shut, so a stale click cannot bounce the operator out.
  useEffect(() => {
    const onOpenGoals = () => {
      if (isActive || isZombieCall) return;
      setWorkspaceView((prev) => {
        if (prev === GOALS_VIEW) return prev; // already open — never bounce out
        saveWorkspaceView("scoreboard");
        return GOALS_VIEW;
      });
    };
    window.addEventListener("cat_open_goals_view", onOpenGoals);
    return () => window.removeEventListener("cat_open_goals_view", onOpenGoals);
  }, [isActive, isZombieCall]);

  // v4.114.0: Greeting Editor view — focused per-greeting workspace, off-call
  // only. Transient like 'goals': refresh returns to the scoreboard.
  const [greetingEditorClip, setGreetingEditorClip] = useState(null);
  const isGreetingEditorView = offCallWorkspace === GREETING_EDITOR_VIEW;

  const onOpenGreetingEditor = useCallback((clipKey = null) => {
    if (isActive || isZombieCall) return;
    setGreetingEditorClip(clipKey || null);
    setWorkspaceView(GREETING_EDITOR_VIEW);
  }, [isActive, isZombieCall]);

  const exitGreetingEditor = useCallback(() => {
    if (isActive || isZombieCall) return;
    saveWorkspaceView("scoreboard");
    setWorkspaceView("scoreboard");
  }, [isActive, isZombieCall]);

  // Studio ↔ Editor: GreetingsPanel dispatches this to open a clip in the editor.
  // v4.167.0: with detail.clipKey it is "open THIS greeting" (from the studio).
  // Without one it is the generic door — which is what Settings → Studio now
  // uses, since the header's ✎ button is gone.
  useEffect(() => {
    const onOpen = (e) => {
      if (isActive || isZombieCall) {
        sayWorkspaceBlocked('The Greeting Editor');
        return;
      }
      onOpenGreetingEditor(e.detail?.clipKey || null);
    };
    window.addEventListener("cat_open_greeting_editor", onOpen);
    return () => window.removeEventListener("cat_open_greeting_editor", onOpen);
  }, [onOpenGreetingEditor, isActive, isZombieCall, sayWorkspaceBlocked]);

  const prepareGuideView = useCallback((action = {}) => {
    if (!action) return;
    if (action.workspace && OFF_CALL_VIEWS.includes(action.workspace)) {
      markStudioHintSeen();
      setShowStudioHint(false);
      saveWorkspaceView(action.workspace);
      setWorkspaceView(action.workspace);
    }
    if (action.settingsSection) {
      setSettingsSection(action.settingsSection);
      setSettingsOpen(true);
    } else if (action.closeSettings) {
      setSettingsOpen(false);
    }
    window.dispatchEvent(new CustomEvent("cat_guide_prepare_view", { detail: action }));
  }, []);

  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
    setShellReady(true);
    // No forced Settings takeover — bottom banner nudges; user opens when ready.
    if (!hasConfiguredDeepgramKey()) return;
    const note = getReleaseNoteForVersion();
    const releaseNotesPending = note && shouldShowReleaseNotes(note);
    if (process.env.NODE_ENV !== "development" && !isAppGuideDone() && !releaseNotesPending) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("cat_open_app_guide"));
      }, 500);
    }
  }, []);

  const [guideOverlayOpen, setGuideOverlayOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => {
      if (process.env.NODE_ENV !== "development") setGuideOverlayOpen(true);
    };
    const onClose = () => setGuideOverlayOpen(false);
    window.addEventListener("cat_open_app_guide", onOpen);
    window.addEventListener("cat_guide_overlay_closed", onClose);
    return () => {
      window.removeEventListener("cat_open_app_guide", onOpen);
      window.removeEventListener("cat_guide_overlay_closed", onClose);
    };
  }, []);

  const blockSecondaryChrome =
    showSplash || !shellReady || settingsOpen || guideOverlayOpen || releaseNotesOpen;

  useEffect(() => {
    applyThemePalette(loadThemePalette());
    const onTheme = (e) => applyThemePalette(e.detail || loadThemePalette());
    window.addEventListener("catint_theme_palette_changed", onTheme);
    return () => window.removeEventListener("catint_theme_palette_changed", onTheme);
  }, []);

  useEffect(() => {
    let objectUrl = null;
    const applyBg = async () => {
      const bgApp = await loadFile("bg_app");
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      const url = bgApp
        ? (objectUrl = generateObjectUrl(bgApp))
        : resolveAppBackgroundPath(null, { advance: true });
      document.body.style.backgroundImage = `url(${url})`;
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundPosition = "center";
      document.body.style.backgroundAttachment = "fixed";
    };
    applyBg();

    window.addEventListener("cat_bg_changed", applyBg);
    return () => {
      window.removeEventListener("cat_bg_changed", applyBg);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  // Matrix Mode Easter Egg
  useEffect(() => {
    const konamiCode = [
      "ArrowUp",
      "ArrowUp",
      "ArrowDown",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "ArrowLeft",
      "ArrowRight",
      "b",
      "a",
    ];
    let index = 0;
    const handleKeyDown = (e) => {
      if (e.key === konamiCode[index]) {
        index++;
        if (index === konamiCode.length) {
          const current = document.body.getAttribute("data-easter-egg");
          document.body.setAttribute(
            "data-easter-egg",
            current === "matrix" ? "" : "matrix",
          );
          index = 0;
        }
      } else {
        index = 0;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // v4.190.0: while the off-call Studio is open, Escape means "exit" and Space
  // means "press the focused button" — the language hotkeys used to eat both
  // (Escape never closed the Studio, and every press silently flipped EN/ES).
  const studioKeysRef = useRef(false);
  studioKeysRef.current = isSoundboardStudio && !isActive;

  // Hotkeys for language switching
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (studioKeysRef.current && (e.code === "Escape" || e.code === "Space")) return;
      const isTyping =
        e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT";

      if (e.code === "Escape" || (e.altKey && e.code === "Space")) {
        e.preventDefault();
        toggleLanguage();
      } else if (e.code === "Space" && !isTyping) {
        e.preventDefault();
        toggleLanguage();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleLanguage]);

  // Track idle time for full app vignette
  const [idleSecs, setIdleSecs] = useState(0);
  useEffect(() => {
    if (isActive || isBreakActive) {
      setIdleSecs(0);
      return;
    }
    const iv = setInterval(() => setIdleSecs((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [isActive, isBreakActive]);

  const isBurnoutWarning = !isBreakActive && minutesSinceLastBreak > 110;
  // PRIORITY FIX: isActive (Call) must always block app-idle vignette
  const stateClass = isActive
    ? "app-active"
    : isBreakActive
      ? "app-break"
      : isBurnoutWarning
        ? "burnout-alert"
        : idleSecs > 45
          ? "app-idle"
          : "";
  const appState =
    isZombieCall && connectionState !== "connected"
      ? "zombie"
      : isActive
        ? "call"
        : isBreakActive
          ? "break"
          : "avail";

  // Audio attached = Deepgram REALLY transcribing + tab stream / mic / cable.
  // v4.153.0: `connectionState === "connected"` used to be enough. Idle-ear and
  // dead pipes report "connected" with no recorder, so CONNECT took the
  // start-the-call branch and the call ran mute. sttLive is the honest gate.
  const audioAttached =
    sttLive && (micTestMode || tabStreamReady || cableStreamReady);

  // v4.153.0: CONNECT self-heal. One press must always end with Deepgram live:
  // if 1.5s after the press nothing is streaming, rebuild once (reuse path —
  // no tab picker, no gesture needed) and never ask again for that press.
  const connectPressedAtRef = useRef(0);
  const connectHealedRef = useRef(false);

  const handleStartCall = useCallback(
    (isRecovery = false) => {
      cancelHipaaDisconnectGrace?.();
      if (isBreakActive) stopBreak();
      if (isRecovery) clearZombieState();
      startSession(isRecovery);
    },
    [startSession, clearZombieState, isBreakActive, stopBreak, cancelHipaaDisconnectGrace],
  );

  // Attach audio then auto-start call (one green-button press).
  const handleAttachAudio = useCallback(
    async (fresh = false) => {
      cancelHipaaDisconnectGrace?.();
      if (isBreakActive) stopBreak();
      // Arm before await — DG text can arrive before isActive React commit.
      armCaptionCapture(true);
      connectPressedAtRef.current = Date.now();
      connectHealedRef.current = false;
      const ok = fresh ? await startRecordingFresh() : await startRecording();
      if (ok) handleStartCall(false);
      else armCaptionCapture(false);
      return ok;
    },
    [
      startRecording,
      startRecordingFresh,
      isBreakActive,
      stopBreak,
      cancelHipaaDisconnectGrace,
      handleStartCall,
      armCaptionCapture,
    ],
  );

  // Zombie refresh: re-attach audio then resume preserved call state.
  const handleRecovery = useCallback(async () => {
    // HIPAA: cancel any pending disconnect grace when resuming a preserved call.
    cancelHipaaDisconnectGrace?.();
    if (isBreakActive) stopBreak();
    let ok = audioAttached;
    if (!ok) {
      armCaptionCapture(true);
      ok = await startRecording();
      if (!ok) armCaptionCapture(false);
    }
    if (ok) handleStartCall(true);
  }, [audioAttached, startRecording, handleStartCall, isBreakActive, stopBreak, cancelHipaaDisconnectGrace, armCaptionCapture]);

  const handleConnectAnotherTab = useCallback(async () => {
    // HIPAA: cancel any pending disconnect grace when attaching a new stream.
    cancelHipaaDisconnectGrace?.();
    if (isBreakActive) stopBreak();
    await startRecordingFresh();
  }, [startRecordingFresh, isBreakActive, stopBreak, cancelHipaaDisconnectGrace]);

  // v4.153.0 CONNECT self-heal — one rebuild per press, never a loop.
  // v4.170.0: it must not fire while the press is still WORKING. The old
  // 1.5s timer went off in the middle of the DG handshake (2-4s is normal),
  // so one press opened two socket pairs and the first pair was never closed.
  // Leaked streams count against the account's concurrent-stream cap, and past
  // that cap Deepgram answers the NEW sockets with silence — the exact
  // "stuck / quiet / ZAP again" loop. `phase === 'connecting'` means that
  // attempt still owns its own stall timers, so wait for it to settle.
  useEffect(() => {
    if (!connectPressedAtRef.current) return undefined;
    const t = setInterval(() => {
      if (
        shouldHealConnect({
          pressedAt: connectPressedAtRef.current,
          sttLive,
          healed: connectHealedRef.current,
          phase: connectProgress?.phase,
        })
      ) {
        connectHealedRef.current = true; // once per press, whatever happens
        startRecording();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [sttLive, startRecording, connectProgress]);

  const AUTO_ATTACH_KEY = "catint_auto_attach_v1";
  const autoAttachAttemptedRef = useRef(false);

  // Auto-attach interpreting tab at/after 09:00 (once per day, off-call only).
  useEffect(() => {
    const tryAutoAttach = async () => {
      if (!autoAttachEnabled) return;
      const now = new Date();
      if (now.getHours() < 9) return;
      if (isActive || isBreakActive || isZombieCall) return;
      if (audioAttached || connectionState === "connecting") return;

      const today = now.toDateString();
      try {
        if (localStorage.getItem(AUTO_ATTACH_KEY) === today) return;
      } catch {}

      if (autoAttachAttemptedRef.current) return;
      autoAttachAttemptedRef.current = true;

      const ok = await startRecordingFresh();
      if (ok) {
        try {
          localStorage.setItem(AUTO_ATTACH_KEY, today);
        } catch {}
      } else {
        autoAttachAttemptedRef.current = false;
      }
    };

    tryAutoAttach();
    // Single-shot attempt. No retry loops: browser tab-capture prompts are blocking.
    return undefined;
  }, [
    isActive,
    isBreakActive,
    isZombieCall,
    audioAttached,
    connectionState,
    startRecordingFresh,
    autoAttachEnabled,
  ]);

  // Hotkeys: C = connect/attach or start call; M = mic test toggle.
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isTyping =
        e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT";
      if (isTyping || e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.code === "KeyM" && !isActive) {
        e.preventDefault();
        setMicTestMode(!micTestMode);
        return;
      }
      // v4.139.0: Settings → Audio mic-mode button bridges here (header pill retired)
      if (e.code === "KeyC" && !isActive && !isBreakActive) {
        e.preventDefault();
        if (isZombieCall) {
          handleRecovery();
        } else if (!audioAttached) {
          handleAttachAudio(false);
        } else {
          handleStartCall(false);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    isActive,
    isBreakActive,
    isZombieCall,
    audioAttached,
    micTestMode,
    setMicTestMode,
    handleAttachAudio,
    handleStartCall,
    handleRecovery,
  ]);

  // v4.139.0: Settings → Audio mic-mode button bridges here (header pill retired).
  // detail.on === false (Tab/VB buttons) exits mic mode; default is enter.
  useEffect(() => {
    const onSetMicMode = (e) => setMicTestMode(e.detail?.on === false ? false : true);
    window.addEventListener("cat_set_mic_mode", onSetMicMode);
    return () => window.removeEventListener("cat_set_mic_mode", onSetMicMode);
  }, [setMicTestMode]);

  // Micro-break nudge: top bar color shifts when working too long without a break
  const micBarColor =
    connectionState === "error"
      ? "#ef4444"
      : isZombieCall && connectionState !== "connected"
      ? "#f59e0b"
      : isActive && minutesSinceLastBreak > 110
        ? "#f59e0b"
        : isActive && minutesSinceLastBreak > 90
          ? "#f59e0b"
          : "#10b981";
  const micBarShadow =
    connectionState === "error"
      ? "0 0 8px #ef4444"
      : isZombieCall && connectionState !== "connected"
        ? "0 0 8px #f59e0b"
      : isActive && minutesSinceLastBreak > 90
        ? "0 0 8px #f59e0b"
        : "0 0 8px #10b981";

  // Off-call idle tick: progressively deeper, but less frequent/less annoying
  const idleMinuteCountRef = useRef(0);
  useEffect(() => {
    if (isActive || isBreakActive) {
      idleMinuteCountRef.current = 0;
      return;
    }
    const iv = setInterval(() => {
      idleMinuteCountRef.current += 1;
      playCoin(idleMinuteCountRef.current);
    }, 90000);
    return () => clearInterval(iv);
  }, [isActive, isBreakActive, playCoin]);

  // Escape exits Soundboard Studio when off-call (skip if editor modal open)
  useEffect(() => {
    if (!isSoundboardStudio || isActive || isZombieCall) return;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector(".sb-editor-overlay")) return;
      exitSoundboardStudio();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSoundboardStudio, isActive, isZombieCall, exitSoundboardStudio]);

  // Escape exits the Goal Tracking view when off-call. (The dial DOES call
  // stopPropagation on Escape as of v4.162.0 — this comment was wrong before
  // that, and one keypress fired both handlers.)
  useEffect(() => {
    if (!isGoalsView || isActive || isZombieCall) return;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      exitGoalsView();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isGoalsView, isActive, isZombieCall, exitGoalsView]);

  // Demo Scenario Trigger (Shift + D)
  useEffect(() => {
    const scenarios = ["call", "goal_hit", "break", "reset"];
    let scenarioIdx = 0;

    const handleKeyDown = (e) => {
      if (e.shiftKey && e.code === "KeyD") {
        const scenario = scenarios[scenarioIdx];
        scenarioIdx = (scenarioIdx + 1) % scenarios.length;

        // Trigger global demo event
        const event = new CustomEvent("cat_demo_scenario", {
          detail: scenario,
        });
        window.dispatchEvent(event);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const renderNotesPanel = (withSoundboardDock = false) => {
    if (!isNotesOpen) return null;
    return (
      <div className="tools-column notes-open">
        {/* v4.110.0: greetings dock above session notes — in-call too.
            Notes only need ~half the column. */}
        {withSoundboardDock && (
          <div className="glass-panel tools-soundboard-dock" data-guide="soundboard-dock" id="soundboard-dock">
            <OnCallSoundboardStrip micTestMode={micTestMode} collapsed={false} onOpenGreetingEditor={isActive ? null : onOpenGreetingEditor} />
          </div>
        )}
        <div
          className="glass-panel tools-notes"
          style={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: isActive && callFocusMode ? "200px" : undefined,
          }}
        >
          {isEditingBg ? (
            <div className="quick-notes-blocked" role="status">
              Quick notes are paused while the soundboard/background editor is open.
              <button type="button" onClick={() => setIsEditingBg(false)}>
                Close editor
              </button>
            </div>
          ) : (
            <>
              {(!isActive || !callFocusMode) && <DictionaryTool />}
              <NotePad />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <ElementHintProvider>
    <GuideHostProvider prepareGuideView={prepareGuideView}>
    <div
      className={`app-container ${stateClass}${shellReady ? " app-shell-ready" : ""}`}
      data-state={appState}
      data-call-mode={isActive || isZombieCall ? "true" : "false"}
      data-off-call-view={isActive || isZombieCall ? "call" : workspaceView}
    >
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}

      <SettingsPanel
        open={settingsOpen}
        onClose={() => {
          setSettingsOpen(false);
          setSettingsOpenReason(null);
        }}
        initialSection={settingsSection}
        openReason={settingsOpenReason}
        openTrigger={settingsOpenTrigger}
      />

      {/* v4.163.0: mid-call only, and bottom-anchored since v4.165.0 (it was
          painted over the sticky row at the top). v4.167.0: generalised to every
          off-call workspace view, because the goal wheel, Soundboard Studio and
          the Greeting Editor are all off-call and all say this one line. */}
      {workspaceBlockedNotice && (isActive || isZombieCall) && (
        <div
          role="status"
          className="goals-blocked-notice"
          onClick={() => setWorkspaceBlockedNotice("")}
        >
          {workspaceBlockedNotice}
        </div>
      )}

      {deepgramKeyNotice && !isActive && !isZombieCall && (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 48,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 99991,
            background: "rgba(15,23,42,0.95)",
            border: "1px solid rgba(245,158,11,0.45)",
            borderRadius: 8,
            padding: "8px 14px",
            fontSize: "0.72rem",
            color: "#fde68a",
            maxWidth: "min(420px, 92vw)",
            lineHeight: 1.4,
            cursor: "pointer",
          }}
          onClick={() => {
            setSettingsSection("deepgram");
            setSettingsOpenReason(getDeepgramBlockReason() || "no_key");
            setSettingsOpenTrigger("connect");
            setSettingsOpen(true);
          }}
        >
          {deepgramKeyNotice}
          <span style={{ display: "block", marginTop: 4, color: "#93c5fd", fontSize: "0.65rem" }}>
            Tap to open Settings → Deepgram
          </span>
        </div>
      )}

      {apiKeyMissing && !isActive && !isZombieCall && !blockSecondaryChrome && (
        <div
          style={{
            position: "fixed",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 99990,
            background: "rgba(245,158,11,0.15)",
            border: "1px solid rgba(245,158,11,0.4)",
            borderRadius: 8,
            padding: "6px 12px",
            fontSize: "0.68rem",
            color: "#fcd34d",
            cursor: "pointer",
          }}
          onClick={() => {
            setSettingsSection("deepgram");
            setSettingsOpenReason(getDeepgramBlockReason() || "no_key");
            setSettingsOpenTrigger("banner");
            setSettingsOpen(true);
          }}
        >
          {getDeepgramBlockReason() === "vault_locked"
            ? "🔐 Saved Deepgram key locked — tap here, then press Unlock"
            : isRememberExpired()
            ? "🔐 Password expired — open Settings (⚙)"
            : "🔑 No STT key in this build — paste in Settings (⚙) or set REACT_APP_DEEPGRAM_API_KEY on Vercel + redeploy"}
        </div>
      )}

      <div
        id="top-mic-bar-container"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          zIndex: 9999,
          pointerEvents: "none",
        }}
      >
        <div
          id="top-mic-bar"
          style={{
            height: "100%",
            width: "0%",
            background: micBarColor,
            transition: "width 0.05s ease-out, background 0.5s ease",
            opacity: 0,
            boxShadow: micBarShadow,
          }}
        />
      </div>

      <SilenceGuardian lastDataTime={lastDataTime} onStopAudio={stopRecording} connectionState={connectionState} />

      {/* v4.98.0: autopilot 10s auto-end countdown banner */}
      <AutopilotGuard onStopAudio={stopRecording} />

      <a href="#main-transcript" className="skip-to-main">
        Skip to transcript
      </a>

      <DashboardHeader
        sttStall={sttStall}
        onAttachAudio={() => handleAttachAudio(false)}
        onAttachAudioFresh={() => handleAttachAudio(true)}
        onStartCall={() => handleStartCall(false)}
        onStopAudio={stopRecording}
        onReconnectStream={reconnectStream}
        onRecovery={handleRecovery}
        onConnectAnotherTab={handleConnectAnotherTab}
        audioAttached={audioAttached}
        sttLanguage={sttLanguage}
        onToggleLanguage={toggleLanguage}
        connectionState={connectionState}
        connectionMessage={connectionMessage}
        apiKeyRejected={apiKeyRejected}
        connectProgress={connectProgress}
        lastDataTime={lastDataTime}
        micTestMode={micTestMode}
        setMicTestMode={setMicTestMode}
        tabStreamReady={tabStreamReady}
        cableStreamReady={cableStreamReady}
        configuredAudioSourceMode={configuredAudioSourceMode}
        attachedAudioSourceMode={attachedAudioSourceMode}
        virtualCableFailure={virtualCableFailure}
        onReconnectAudioSource={() => switchAudioSourceModeSafely?.(configuredAudioSourceMode)}
        onSwitchToTabShare={async () => {
          const ok = await switchAudioSourceModeSafely?.("tab");
          if (ok) switchAudioSourceMode("tab");
          return ok;
        }}
        onSwitchToVirtualCable={async () => {
          const ok = await switchAudioSourceModeSafely?.("virtualCable");
          if (ok) switchAudioSourceMode("virtualCable");
          return ok;
        }}
        offCallWorkspace={offCallWorkspace}
        onCycleWorkspace={cycleWorkspaceView}
        showStudioHint={showStudioHint}
        settingsOpen={settingsOpen}
        onOpenGoalsView={onOpenGoalsView}
        goalsOpen={isGoalsView}
        onOpenGreetingEditor={onOpenGreetingEditor}
        greetingEditorOpen={isGreetingEditorView}
        onOpenSoundboard={() => {
          if (workspaceView === "soundboard") {
            exitSoundboardStudio();
          } else {
            saveWorkspaceView("soundboard");
            setWorkspaceView("soundboard");
          }
        }}
        soundboardOpen={isSoundboardStudio}
      />

      {!(isActive || isZombieCall) && workspaceView === "scoreboard" && (
        <OffCallWorkspace
          audioAttached={audioAttached}
          micTestMode={micTestMode}
          audioSourceMode={configuredAudioSourceMode}
          connectionState={connectionState}
          connectionMessage={connectionMessage}
          connectProgress={connectProgress}
          tabStreamReady={tabStreamReady}
          cableStreamReady={cableStreamReady}
          isZombieCall={isZombieCall}
          isBreakActive={isBreakActive}
          settingsOpen={settingsOpen}
          notesOpen={isNotesOpen}
          notesPanel={renderNotesPanel(true)}
        />
      )}

      {isSoundboardStudio && (
        <main className={`main-content view-soundboard${isNotesOpen ? " notes-open" : ""}`}>
          <div
            className="workspace-soundboard-pane glass-panel"
            id="workspace-soundboard-pane"
            data-guide="soundboard-lab"
          >
            <GreetingsPanel
              micTestMode={micTestMode}
              onEditModeChange={setIsEditingBg}
              onExitStudio={exitSoundboardStudio}
            />
          </div>
          {renderNotesPanel()}
        </main>
      )}

      {/* v4.113.0: Goal Tracking view — dial + month calendar, off-call only */}
      {isGoalsView && (
        <main className={`main-content view-goals${isNotesOpen ? " notes-open" : ""}`}>
          <GoalTrackingView onExit={exitGoalsView} />
        </main>
      )}

      {/* v4.114.0: Greeting Editor view — one greeting at a time, off-call only */}
      {isGreetingEditorView && (
        <main className={`main-content view-greeting-editor${isNotesOpen ? " notes-open" : ""}`}>
          <GreetingEditorView
            initialClipKey={greetingEditorClip}
            onExit={exitGreetingEditor}
            onOpenStudio={() => setWorkspaceView("soundboard")}
            micTestMode={micTestMode}
          />
        </main>
      )}

      {(isActive || isZombieCall || hipaaGraceActive) && (
        <main id="main-transcript" className={`main-content ${isNotesOpen ? "notes-open" : ""}`}>
          {/* v4.110.0: fallback strip only when notes are closed — with notes
              open, greetings live in the rail dock instead of a full-width row. */}
          {isActive && !isNotesOpen && <OnCallSoundboardStrip micTestMode={micTestMode} onOpenGreetingEditor={null} />}
          <div className="transcription-pane" data-guide="transcript">
            {/* v4.179.0: transcription stopped = loud red bar, never silent */}
            <SttStallBanner reason={sttStall} onZap={reconnectStream} />
            {/* v4.193.0: hold study overlay removed — it popped up mid-call on short pauses. Hold = header H only. */}
            <TranscriptionBoard
              captions={captions}
              isActive={isActive}
              isBreakActive={isBreakActive}
              connectionState={connectionState}
              audioAttached={audioAttached}
              micTestMode={micTestMode}
              audioSourceMode={configuredAudioSourceMode}
              onClearAll={clearCaptions}
              onReconnect={handleRecovery}
              lastDataTime={lastDataTime}
              connectProgress={connectProgress}
            />
          </div>
          {renderNotesPanel(true)}
        </main>
      )}

      {/* v4.126.0: never offer a reload mid-call — location.reload() kills
          the tab stream + DG sockets and the re-attach picker loop reads as
          "hanging". The flag persists, so the banner returns after STOP. */}
      <UpdateAppBanner
        show={updateAvailable && shellReady && !blockSecondaryChrome && isAppGuideDone() && hasConfiguredDeepgramKey() && !isActive && !isZombieCall && !isBreakActive}
        latestVersionToken={latestVersionToken}
        onDismiss={dismissUpdate}
        onUpdate={reloadToUpdate}
      />

      <ReleaseNotesModal
        open={releaseNotesOpen && shellReady && !isActive && !isZombieCall}
        note={releaseNote}
        onGotIt={onReleaseNotesGotIt}
        onLater={onReleaseNotesLater}
        onNever={onReleaseNotesNever}
      />

      {quickNotesNotice && (
        <div className="quick-notes-status" role="status" aria-live="polite">
          {quickNotesNotice}
        </div>
      )}

      <div className="wellbeing-dock habit-dock">
        {showWellbeingWidgets && (
          <>
            <DeskExerciseWidget />
            <RosaryWidget />
            <MealTrackerWidget />
            <ChoreTrackerWidget />
          </>
        )}
        {/*
          Soundboard access is already provided by the top bar button
          (`AudioRouteStatusBar`), so we intentionally remove the bottom
          WorkspaceViewSwitcher pill to avoid duplicate CTAs.
        */}
        <button
          id="quick-notes-btn"
          data-guide="notes"
          data-tooltip="Quick Notes"
          aria-label="Quick Notes"
          onClick={toggleQuickNotes}
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "20px",
            border: "1px solid rgba(255,255,255,0.1)",
            background: isNotesOpen
              ? "rgba(244, 63, 94, 0.25)"
              : "rgba(7, 14, 35, 0.7)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            color: isNotesOpen ? "#f87171" : "rgba(255,255,255,0.6)",
            fontSize: "0.85rem",
            fontWeight: 800,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: isNotesOpen
              ? "0 0 12px rgba(244, 63, 94, 0.3)"
              : "0 2px 8px rgba(0,0,0,0.3)",
            transition: "all 0.3s ease",
            padding: 0,
            flexShrink: 0,
          }}
        >
          📝
        </button>
      </div>
    </div>
    <HudInspectorHost />
    <SttAudioRecordingIndicator />
    <SttDiagnosticInspector />
    </GuideHostProvider>
    </ElementHintProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <AudioSettingsProvider>
        <SessionProvider>
          <Dashboard />
        </SessionProvider>
      </AudioSettingsProvider>
    </AuthProvider>
  );
}

export default App;
